import { useTheme } from '@fluentui/react';
import { IPIcon, IPIconButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { cancelIcon, globalStackTokensGapMedium } from 'globalStyles';
import { CopilotConversation, CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import { CopilotDocumentSelection } from 'models/copilot/copilotDocumentSelection';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CopilotChat } from '../Panel/Conversation/CopilotChat';
import { CopilotChatInput } from '../Panel/Conversation/CopilotChatInput';
import { getConversationWithoutDocumentBlocks } from '../Panel/Documents/copilotDocumentBlockHelpers';
import { CopilotStreamState } from '../Panel/Runtime/copilotStreamReducer';

// Max characters of the selected text shown in the chip; the full text is available via the title tooltip.
const SELECTION_PREVIEW_MAX = 60;

// Collapse whitespace and truncate the selected text to a single short preview line for the chip.
const selectionPreview = (text: string): string => {
  const oneLine = text.replace(/\s+/g, ' ').trim();

  return oneLine.length > SELECTION_PREVIEW_MAX ? `${oneLine.slice(0, SELECTION_PREVIEW_MAX)}...` : oneLine;
};

interface ICopilotAgentChatProps {
  conversation: CopilotConversation | undefined;
  // the Copilot session of the conversation, for the download of a File block
  sessionId?: number;
  isStreaming: boolean;
  isLoading: boolean;
  streamState: CopilotStreamState;
  chatDisabled?: boolean;
  welcomeMessage?: string;
  onNewQuestion: (question: string) => void | Promise<void>;
  onStop: () => void;
  onEntitySelection: (selectedEntityIds: number[]) => void | Promise<void>;
  onAdaptiveInputsChanged: (
    itemId: string | undefined,
    blockId: string | undefined,
    inputs: Record<string, string>,
  ) => void;
  onAdaptiveSubmit: (
    itemId: string | undefined,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
  ) => void | Promise<void>;
  onApprovalDetails: (
    itemId: string | undefined,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
  ) => void;
  // absent: the last question cannot be edited
  onEditLastQuestion?: (itemId: string | undefined, question: string) => void | Promise<void>;
  // Current document text selection to show as a dismissable chip above the input, or null/undefined for none.
  selection?: CopilotDocumentSelection | null;
  // Clears the selection when the user dismisses the chip.
  onClearSelection?: () => void;
}

// Self-contained Copilot chat surface (thread + streaming + input), driven by useCopilotAgentSession.
// Hosted beside the thing the agent works on: the document editor and the evidence pane of a task modal.
// Document content blocks are stripped from the thread because the document itself is shown beside the chat.
export const CopilotAgentChat = (props: ICopilotAgentChatProps): JSX.Element => {
  const { t } = useTranslation(['copilot']);
  const theme = useTheme();
  const chatConversation = useMemo(
    () => getConversationWithoutDocumentBlocks(props.conversation),
    [props.conversation],
  );

  return (
    <IPStack
      verticalFill
      tokens={globalStackTokensGapMedium}
      styles={{ root: { height: '100%', minHeight: 0, overflow: 'hidden', width: '100%' } }}
    >
      <IPStack.Item grow styles={{ root: { display: 'flex', flexBasis: 0, minHeight: 0, overflow: 'hidden' } }}>
        <CopilotChat
          conversation={chatConversation}
          sessionId={props.sessionId}
          isStreaming={props.isStreaming}
          streamText={props.streamState.chatText}
          streamStatus={props.streamState.status}
          agentEvents={props.streamState.events}
          welcomeMessage={props.welcomeMessage}
          onEntitySelection={props.onEntitySelection}
          onAdaptiveInputsChanged={props.onAdaptiveInputsChanged}
          onAdaptiveSubmit={props.onAdaptiveSubmit}
          onApprovalDetails={props.onApprovalDetails}
          onEditLastQuestion={props.onEditLastQuestion}
        />
      </IPStack.Item>
      <IPStack.Item
        styles={{
          root: { alignSelf: 'stretch', display: 'flex', flexDirection: 'column', flexShrink: 0, width: '100%' },
        }}
      >
        {props.selection && (
          <IPStack
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{
              root: {
                marginBottom: 6,
                padding: '4px 8px',
                borderRadius: 4,
                background: theme.palette.neutralLighter,
              },
            }}
          >
            <IPIcon iconName="Highlight" styles={{ root: { fontSize: 14, color: theme.palette.neutralSecondary } }} />
            <IPText variant="small" styles={{ root: { flexShrink: 0, color: theme.palette.neutralSecondary } }}>
              {t('copilot:Chat.Document.Selection.ChipLabel', { count: props.selection.text.length })}
            </IPText>
            <IPText
              variant="small"
              title={props.selection.text}
              styles={{
                root: { flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              }}
            >
              {selectionPreview(props.selection.text)}
            </IPText>
            <IPIconButton
              iconProps={cancelIcon}
              title={t('copilot:Chat.Document.Selection.Remove')}
              onClick={props.onClearSelection}
              styles={{ root: { height: 24, width: 24 } }}
            />
          </IPStack>
        )}
        <CopilotChatInput
          onNewQuestion={props.onNewQuestion}
          chatDisabled={props.chatDisabled ?? false}
          isWaitingForProcess={props.isStreaming}
          isBusy={props.isStreaming}
          attachmentMode={false}
          canSubmit={false}
          hasAttachments={false}
          isLoading={props.isLoading}
          onStop={props.onStop}
          canCancel={props.isStreaming && !props.isLoading}
          showWorkingPlaceholder={props.isStreaming}
          targetFolder={undefined}
        />
      </IPStack.Item>
    </IPStack>
  );
};
